import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { motion, useReducedMotion } from "motion/react";
import {
  ChevronDown,
  ImageOff,
  Martini,
  Utensils,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  displayPrice,
  GROUP_LABELS,
  groupProducts,
  productId
} from "../lib/cartaHelpers";
import "./Menu.css";

export default function Menu() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [activeGroup, setActiveGroup] = useState("bebida");
  const [activeCategory, setActiveCategory] = useState("");
  const [selectedProduct, setSelectedProduct] = useState(null);
  const mainRef = useRef(null);
  const cooldownRef = useRef(false);
  const sentinelRef = useRef(null);
  const navRef = useRef(null);
  const [mobileNavFixed, setMobileNavFixed] = useState(false);

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    fetch("/api/products/public", { signal: controller.signal })
      .then((res) => {
        if (!res.ok) throw new Error("Error cargando carta");
        return res.json();
      })
      .then((data) => {
        setProducts(Array.isArray(data) ? data : []);
        setError("");
      })
      .catch((err) => {
        if (err.name !== "AbortError") setError(err.message || "No se pudo cargar la carta");
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });

    return () => controller.abort();
  }, []);

  const sections = useMemo(() => groupProducts(products), [products]);
  const counts = useMemo(
    () =>
      sections.reduce(
        (acc, section) => {
          acc[section.meta.group] += section.items.length;
          return acc;
        },
        { bebida: 0, comida: 0 }
      ),
    [sections]
  );

  const visibleSections = useMemo(
    () => sections.filter((section) => section.meta.group === activeGroup),
    [activeGroup, sections]
  );

  useEffect(() => {
    if (!visibleSections.length) return;
    if (!visibleSections.some((section) => section.category === activeCategory)) {
      setActiveCategory(visibleSections[0].category);
    }
  }, [activeCategory, visibleSections]);

  // Mobile: fix sticky nav at top when hero scrolls out of view
  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (window.innerWidth < 768) {
          setMobileNavFixed(!entry.isIntersecting && entry.boundingClientRect.top < 0);
        }
      },
      { threshold: 0 }
    );
    observer.observe(sentinel);
    const onResize = () => { if (window.innerWidth >= 768) setMobileNavFixed(false); };
    window.addEventListener("resize", onResize);
    return () => { observer.disconnect(); window.removeEventListener("resize", onResize); };
  }, []);


  const scrollToSection = useCallback(() => {
    if (!mainRef.current) return;
    const top = mainRef.current.getBoundingClientRect().top + window.scrollY - 66;
    window.scrollTo({ top: Math.max(0, top), behavior: "smooth" });
  }, []);

  const scrollToCategory = useCallback((category) => {
    setActiveCategory(category);
    setTimeout(scrollToSection, 60);
  }, [scrollToSection]);

  const selectGroup = useCallback((group) => {
    setActiveGroup(group);
    const first = sections.find((s) => s.meta.group === group);
    if (first) scrollToCategory(first.category);
  }, [sections, scrollToCategory]);



  return (
    <div className="carta-page">
      <CartaHero
        total={products.length}
        loading={loading}
        onDrink={() => selectGroup("bebida")}
        onFood={() => selectGroup("comida")}
      />

      <div ref={sentinelRef} style={{ height: 0, pointerEvents: "none" }} aria-hidden="true" />
      <CartaStickyNav
        activeGroup={activeGroup}
        activeCategory={activeCategory}
        counts={counts}
        sections={visibleSections}
        onGroupChange={selectGroup}
        onCategoryClick={scrollToCategory}
        navRef={navRef}
        mobileFixed={mobileNavFixed}
      />
      {mobileNavFixed && <div className="carta-nav-ph" aria-hidden="true" />}

      <main className="carta-main" ref={mainRef}>
        {loading && <CartaStatus title="Cargando carta" text="Preparando productos y precios actualizados." />}
        {error && <CartaStatus tone="error" title="No se pudo cargar la carta" text={error} />}
        {!loading && !error && sections.length === 0 && (
          <CartaStatus title="Carta no disponible" text="No hay productos activos ahora mismo." />
        )}

        {!loading && !error && (() => {
          const currentIdx = visibleSections.findIndex((s) => s.category === activeCategory);
          const current = visibleSections[currentIdx];
          if (!current) return null;
          const hasPrev = currentIdx > 0;
          const hasNext = currentIdx < visibleSections.length - 1;
          return (
            <>
              <CartaSection
                key={current.category}
                refNode={null}
                section={current}
                index={0}
                onProductClick={setSelectedProduct}
              />
              <div className="carta-section-nav">
                {hasPrev ? (
                  <button
                    className="carta-nav-btn carta-nav-prev"
                    onClick={() => scrollToCategory(visibleSections[currentIdx - 1].category)}
                  >
                    ↑ {visibleSections[currentIdx - 1].meta.label}
                  </button>
                ) : <span />}
                <span className="carta-nav-pos">
                  {currentIdx + 1} / {visibleSections.length}
                </span>
                {hasNext ? (
                  <button
                    className="carta-nav-btn carta-nav-next"
                    onClick={() => scrollToCategory(visibleSections[currentIdx + 1].category)}
                  >
                    {visibleSections[currentIdx + 1].meta.label} ↓
                  </button>
                ) : <span />}
              </div>
            </>
          );
        })()}
      </main>

      <CartaFooterCTA />
      <CartaProductModal product={selectedProduct} onClose={() => setSelectedProduct(null)} />
    </div>
  );
}

function CartaHero({ total, loading, onDrink, onFood }) {
  const reduce = useReducedMotion();

  return (
    <header className="carta-hero">
      <div className="carta-hero-bg" aria-hidden="true" />
      <motion.div
        className="carta-hero-inner"
        initial={reduce ? false : { opacity: 0, y: 18 }}
        animate={reduce ? undefined : { opacity: 1, y: 0 }}
        transition={{ duration: 0.28, ease: "easeOut" }}
      >
        <div className="carta-neon-frame">
          <span className="carta-side-line carta-side-line-left" aria-hidden="true" />
          <span className="carta-side-line carta-side-line-right" aria-hidden="true" />
          <p className="carta-kicker">NEON MUSIC &amp; COCKTAIL</p>
          <h1>CARTA</h1>
          <p className="carta-claim">Sabores que suenan bien</p>
          <div className="carta-hero-rule" aria-hidden="true" />
          <p className="carta-hero-copy">
            Cocktails, copas, desayunos y platos para compartir con la misma luz de la carta fisica.
          </p>
          <div className="carta-hero-actions">
            <Button className="carta-btn carta-btn-cyan" onClick={onDrink}>
              <Martini size={18} />
              Bebidas
            </Button>
            <Button className="carta-btn carta-btn-pink" onClick={onFood}>
              <Utensils size={18} />
              Comida
            </Button>
          </div>
          <div className="carta-hero-meta">
            <span>{loading ? "Actualizando carta" : `${total} productos`}</span>
            <ChevronDown size={16} aria-hidden="true" />
          </div>
        </div>
      </motion.div>
    </header>
  );
}

function CartaStickyNav({
  activeGroup,
  activeCategory,
  counts,
  sections,
  onGroupChange,
  onCategoryClick,
  navRef,
  mobileFixed,
}) {
  return (
    <nav
      ref={navRef}
      className={`carta-sticky-nav${mobileFixed ? " is-mobile-fixed" : ""}`}
      aria-label="Navegacion de carta"
    >
      <div className="carta-nav-inner">
        <Tabs value={activeGroup} onValueChange={onGroupChange} className="carta-mode-tabs">
          <TabsList className="carta-mode-list" aria-label="Tipo de carta">
            {Object.entries(GROUP_LABELS).map(([group, label]) => (
              <TabsTrigger
                key={group}
                value={group}
                className="carta-mode-trigger"
                disabled={counts[group] === 0}
              >
                {label}
                <span>{counts[group]}</span>
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>

        <CartaCategoryTabs
          sections={sections}
          activeCategory={activeCategory}
          onCategoryClick={onCategoryClick}
        />
      </div>
    </nav>
  );
}

function CartaCategoryTabs({ sections, activeCategory, onCategoryClick }) {
  if (!sections.length) return null;

  return (
    <div className="carta-category-tabs" aria-label="Categorias">
      {sections.map((section) => {
        const Icon = section.meta.icon;
        const active = section.category === activeCategory;
        return (
          <button
            key={section.category}
            type="button"
            className={`carta-category-tab${active ? " is-active" : ""}`}
            onClick={() => onCategoryClick(section.category)}
            aria-current={active ? "true" : undefined}
          >
            {active && <motion.span className="carta-tab-indicator" layoutId="carta-tab-indicator" />}
            <Icon size={15} aria-hidden="true" />
            <span>{section.meta.label}</span>
          </button>
        );
      })}
    </div>
  );
}

function CartaSection({ section, refNode, index, onProductClick }) {
  const reduce = useReducedMotion();
  const Icon = section.meta.icon;

  const isExtras = (p) => p.name.startsWith("Extra:");
  const mainItems  = section.items.filter((p) => !isExtras(p));
  const extraItems = section.items.filter(isExtras);

  return (
    <motion.section
      ref={refNode}
      data-category={section.category}
      className="carta-section"
      initial={false}
      whileInView={reduce ? undefined : { y: 0 }}
      viewport={{ once: true, amount: 0.12 }}
      transition={{ duration: 0.24, delay: Math.min(index * 0.03, 0.18) }}
    >
      <div className="carta-section-head">
        <div>
          <p>{mainItems.length} opciones</p>
          <h2>
            <Icon size={22} aria-hidden="true" />
            {section.meta.label}
          </h2>
        </div>
        <span className="carta-section-code">{section.meta.short}</span>
      </div>

      <div className="carta-products-grid">
        {mainItems.map((product, itemIndex) => (
          <CartaProductCard
            key={productId(product)}
            product={product}
            meta={section.meta}
            index={itemIndex}
            onProductClick={onProductClick}
          />
        ))}
      </div>

      {extraItems.length > 0 && (
        <div className="carta-extras-section">
          <h3 className="carta-extras-title">Personaliza tu hamburguesa</h3>
          <div className="carta-extras-grid">
            {extraItems.map((extra) => (
              <button
                key={productId(extra)}
                className="carta-extra-chip"
                onClick={() => onProductClick && onProductClick(extra)}
                type="button"
              >
                <span className="carta-extra-name">
                  {extra.name.replace("Extra: ", "")}
                </span>
                <span className="carta-extra-price">+{Number(extra.price).toFixed(2)} €</span>
              </button>
            ))}
          </div>
        </div>
      )}
    </motion.section>
  );
}

function CartaProductCard({ product, meta, index, onProductClick }) {
  const reduce = useReducedMotion();
  const [imageError, setImageError] = useState(false);
  const cardImage = product.thumbnailImageUrl || product.imageUrl || product.detailImageUrl;
  const hasImage = cardImage && !imageError;
  const tags = Array.isArray(product.tags) ? product.tags : [];
  const allergens = Array.isArray(product.allergens) ? product.allergens : [];

  return (
    <motion.article
      className={`carta-product-card carta-product-${meta.group}`}
      onClick={() => onProductClick && onProductClick(product)}
      style={{ cursor: onProductClick ? "pointer" : undefined }}
      initial={false}
      whileInView={reduce ? undefined : { y: 0 }}
      viewport={{ once: true, amount: 0.18 }}
      transition={{ duration: 0.22, delay: Math.min(index * 0.018, 0.18) }}
    >
      <div className="carta-product-media">
        {hasImage ? (
          <img
            src={cardImage}
            alt={product.name}
            loading="lazy"
            width="112"
            height="112"
            onError={() => setImageError(true)}
          />
        ) : (
          <div className="carta-product-fallback" aria-hidden="true">
            <ImageOff size={22} />
            <span>{meta.short}</span>
          </div>
        )}
      </div>

      <div className="carta-product-body">
        <div className="carta-product-title-row">
          <h3>{product.name}</h3>
          {product.featured && <span className="carta-featured">Firma</span>}
        </div>
        {product.description && <p className="carta-product-desc">{product.description}</p>}
        {(tags.length > 0 || allergens.length > 0) && (
          <div className="carta-product-tags">
            {tags.slice(0, 3).map((tag) => (
              <span key={tag}>{tag}</span>
            ))}
            {allergens.slice(0, 2).map((allergen) => (
              <span key={allergen} className="is-warn">
                {allergen}
              </span>
            ))}
          </div>
        )}
      </div>

      <div className="carta-product-price">{displayPrice(product)}</div>
    </motion.article>
  );
}

function CartaStatus({ title, text, tone = "default" }) {
  return (
    <div className={`carta-status carta-status-${tone}`}>
      <span aria-hidden="true" />
      <h2>{title}</h2>
      <p>{text}</p>
    </div>
  );
}

function CartaProductModal({ product, onClose }) {
  const [imgError, setImgError] = useState(false);

  useEffect(() => {
    if (!product) return;
    setImgError(false);
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [product, onClose]);

  if (!product) return null;

  const modalImage = product.detailImageUrl || product.imageUrl || product.thumbnailImageUrl;
  const imgSrc = modalImage && !imgError
    ? modalImage
    : "/assets/neon/no-image-neon.webp";
  const tags = Array.isArray(product.tags) ? product.tags : [];

  return (
    <div className="carta-modal-overlay" onClick={onClose} role="dialog" aria-modal="true">
      <div className="carta-modal" onClick={(e) => e.stopPropagation()}>
        <button className="carta-modal-close" onClick={onClose} aria-label="Cerrar">✕</button>

        <div className="carta-modal-media">
          <img
            src={imgSrc}
            alt={product.name}
            loading="lazy"
            onError={() => setImgError(true)}
          />
        </div>

        <div className="carta-modal-body">
          <div className="carta-modal-header">
            <div className="carta-modal-title-wrap">
              <h2 className="carta-modal-name">{product.name}</h2>
              {product.featured && <span className="carta-featured">Firma</span>}
            </div>
            <div className="carta-modal-price">{displayPrice(product)}</div>
          </div>

          {product.description && (
            <p className="carta-modal-desc">{product.description}</p>
          )}

          {tags.length > 0 && (
            <div className="carta-modal-tags">
              <p className="carta-modal-tags-label">Ingredientes</p>
              <div className="carta-product-tags">
                {tags.map((t) => <span key={t}>{t}</span>)}
              </div>
            </div>
          )}

          {Array.isArray(product.variants) && product.variants.length > 0 && (
            <div className="carta-modal-variants">
              <p className="carta-modal-variants-label">Formatos disponibles</p>
              <div className="carta-modal-variants-list">
                {product.variants.map((variant, index) => (
                  <span key={`${variant.label}-${index}`} className="carta-modal-variant-item">
                    {variant.label} - {Number(variant.price).toFixed(2)} €
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}


function CartaFooterCTA() {

  return (
    <footer className="carta-footer-cta">
      <div className="carta-footer-line" aria-hidden="true" />
      <h2>NEON Music &amp; Cocktail</h2>
      <p>C. de la Coruna, 16, Alcala de Henares</p>
      <a
        href="https://wa.me/34614807628?text=Hola!%20Quiero%20reservar%20en%20NE%C3%96N"
        target="_blank"
        rel="noreferrer"
      >
        Reservar por WhatsApp
      </a>
    </footer>
  );
}
